import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { BehaviorSubject } from "rxjs";
import { NdmsSafeSubscriber } from '../../../shared/core/interfaces/ndms-safe-subscriber';
import { ParsConstants } from './pars-constants.service';
import { EventService } from '../api/services/event.service';
import { EventModel } from '../api/models/event-model';
import { EventLocationModel } from '../api/models/event-location-model';
import { EventLocationService } from '../api/services/event-location.service';
import { LookupService } from 'projects/shared/core/services/lookup.service';
import { UserService } from '../api/services/user.service';
import { UserModelEditorViewModel } from '../api/models/user-model-editor-view-model';
import { EventAttendanceModel } from '../api/models/event-attendance-model';
import { EventAttendanceService } from '../api/services/event-attendance.service';
import { ResponderCurrentAttendanceModel } from '../api/models/responder-current-attendance-model';
import { ResponderCurrentAttendanceService } from '../api/services/responder-current-attendance.service';
import { UserSettingModel } from '../api/models/user-setting-model';
import { UserSettingsService } from '../api/services/user-settings.service';
import { FindFilter } from '../api/models/find-filter';

@Injectable({
  providedIn: 'root'
})
export class ParsEventService extends NdmsSafeSubscriber {
  private _currentEvent$: BehaviorSubject<EventModel> = new BehaviorSubject<EventModel>({});
  get currentEvent$() {
    return this._currentEvent$.asObservable();
  }
  get currentEvent() {
    return this._currentEvent$.value;
  }

  private _currentUser$: BehaviorSubject<UserModelEditorViewModel> = new BehaviorSubject<UserModelEditorViewModel>(undefined);
  get currentUser() {
    return this._currentUser$.value;
  }

  private _currentUserSetting$: BehaviorSubject<UserSettingModel> = new BehaviorSubject<UserSettingModel>(undefined);
  get currentUserSetting() {
    return this._currentUserSetting$.value;
  }

  private _locations$: BehaviorSubject<EventLocationModel[]> = new BehaviorSubject<EventLocationModel[]>([]);
  get locations$() {
    return this._locations$.asObservable();
  }
  get locations() {
    return this._locations$.value;
  }

  private _attendances$: BehaviorSubject<EventAttendanceModel[]> = new BehaviorSubject<EventAttendanceModel[]>([]);
  get attendances$() {
    return this._attendances$.asObservable();
  }
  get attendances() {
    return this._attendances$.value;
  }

  private _currentAttendances$: BehaviorSubject<ResponderCurrentAttendanceModel[]> = new BehaviorSubject<ResponderCurrentAttendanceModel[]>([]);
  get currentAttendances$() {
    return this._currentAttendances$.asObservable();
  }
  get currentAttendances() {
    return this._currentAttendances$.value;
  }

  constructor(
    private _eventService: EventService,
    private _userSettingService: UserSettingsService,
    private _userService: UserService,
    private _eventLocationService: EventLocationService,
    private _eventAttendanceService: EventAttendanceService,
    private _responderCurrentAttendanceService: ResponderCurrentAttendanceService,
    private _parsConstants: ParsConstants,
    private _lookupService: LookupService) {
    super();
  }

  async resolve(route) {
    new Promise<any>(async (resolve, reject) => {
      this.loadCurrentEvent().then((result) => {
        if (!result) {
          this._currentEvent$.next(undefined);
        }
        return resolve(result);
      }
      );
    });
  }

  clearCurrentEvent(event: EventModel) {
    return new Promise<EventModel>((resolve, reject) => {
      this._currentEvent$.next(undefined);
      this.loadCurrentUser()
        .then(user => this.loadCurrentUserSettings(user))
        .then(settings => {
          if (settings === undefined) {
            return resolve(event);
          } else {
            settings.parsCurrentEventId = null;
            this.safeSubscribe(this._userSettingService.Update(settings),
              () => resolve(event),
              error => reject(error));
          }
        });
    });
  }

  loadCurrentEvent() {
    return new Promise<any>((resolve, reject) => {
      this.loadCurrentUser()
        .then(user => this.loadCurrentUserSettings(user))
        .then(settings => {
          if (settings === undefined || !settings.parsCurrentEventId) {
            return resolve(undefined);
          } else {
            this.safeSubscribe(this._eventService.FindInDomain({
              findKeys: {
                "Id": settings.parsCurrentEventId.toString()
              },
              includes: ["EventType"],
            }), data => {
              var foundEvent = data && data.length > 0;

              this._currentEvent$.next(foundEvent ? data[0] : undefined);
              return resolve(foundEvent ? data[0] : undefined);
            }, error => reject(error));
          }
        });
    });
  }

  setCurrentEvent(event: EventModel) {
    return new Promise<EventModel>((resolve, reject) => {
      this._currentEvent$.next(event);
      this.loadCurrentUser()
        .then(user => this.loadCurrentUserSettings(user))
        .then(settings => {
          if (settings === undefined && this.currentUser !== undefined) {
            this.safeSubscribe(this._userSettingService.Add({
              active: true,
              parsCurrentEventId: event.id,
              userId: this.currentUser.model.id
            }),
              () => resolve(event),
              error => reject(error));
          } else {
            settings.parsCurrentEventId = event.id;
            this.safeSubscribe(this._userSettingService.Update(settings),
              () => resolve(event),
              error => reject(error));
          }
        });
    });
  }

  getEvent(eventid: number) {
    return new Promise<EventModel>((resolve, reject) => {
      this.safeSubscribe(this._eventService.FindInDomain({ findKeys: { "Id": eventid.toString() } }), data => {
        if (data.length > 0) {
          return resolve(data[0]);
        }
        resolve(undefined);
      }, error => reject(error));
    });
  }

  loadEventLookups() {
    return new Promise((resolve, reject) => {
      this.safeSubscribe(this._eventService.Lookups(),
        data => {
          this._lookupService.loadLookups(data);
          return resolve(data);
        },
        error => {
          reject(error);
        });
    });
  }

  loadEventLocations() {
    return new Promise<EventLocationModel[]>((resolve, reject) => {
      if (!this.currentEvent) {
        return reject("No current event set for data load");
      }
      const findFilter = {
        findKeys: {
          "Active": "true",
          "EventId": this.currentEvent.id.toString()
        },
        includes: ["StateName"],
        sortInfos: [
          {
            columnName: "Name",
            isDescending: false
          }
        ],
        pagingInfo: {
          "pageNumber": 0,
          "pageSize": 2000
        }
      };
      this.safeSubscribe(this._eventLocationService.FindInDomain(findFilter), data => {
        this._locations$.next(data);
        this._lookupService.set("EventLocations", this.locations.map(location => {
          return { id: location.id, display: location.name };
        }));
        resolve(data);
      }, error => {
        reject(error);
      });
    });
  }

  loadCurrentAttendanceAtEventByResponder(responderId: number, eventId: number) {
    return new Promise<ResponderCurrentAttendanceModel>((resolve, reject) => {
      this.safeSubscribe(this._responderCurrentAttendanceService.FindInDomain({
        findKeys: {
          "EventId": eventId.toString(),
          "ResponderId": responderId.toString(),
          "ParsControllerId": this.currentUserControllerId() ? this.currentUserControllerId().toString() : ""
        },
        includes: ["CurrentEventAttendance"],
        pagingInfo: {
          "pageNumber": 0,
          "pageSize": 10000
        },
        sortInfos: [
          {
            columnName: "UpdatedOn",
            isDescending: true
          }
        ]
      }), data => {
        if (data && data.length > 0) {
          return resolve(data[0]);
        }
        return resolve(undefined);
      }, error => {
        reject(error);
      });
    });
  }

  loadAttendancesAtEvent() {
    return new Promise<EventAttendanceModel[]>((resolve, reject) => {
      if (!this.currentEvent) {
        return reject("No current event set for data load");
      }
      const findFilter = {
        findKeys: {
          "EventId": this.currentEvent.id.toString(),
        },
        includes: ["Agency", "ParsController", "ParsStatus"],
        pagingInfo: {
          "pageNumber": 0,
          "pageSize": 10000
        },
        sortInfos: [
          {
            columnName: "UpdatedOn",
            isDescending: true
          }
        ]
      };
      this.safeSubscribe(this._eventAttendanceService.FindInDomain(findFilter), data => {
        this._attendances$.next(data);
        return resolve(data);
      }, error => {
        reject(error);
      });
    });
  }

  loadCurrentAttendancesAtEvent(controllerId: number, teamName: string) {
    return new Promise<ResponderCurrentAttendanceModel[]>((resolve, reject) => {
      if (!this.currentEvent) {
        return reject("No current event set for data load");
      }

      let filter: FindFilter = {
        findKeys: {
          "EventId": this.currentEvent.id.toString()
        },
        includes: [
          "CurrentEventAttendance",
          "CurrentEventAttendance.Agency",
          "CurrentEventAttendance.IcsPosition",
          "CurrentEventAttendance.ParsController",
          "CurrentEventAttendance.ParsStatus",
          "CurrentEventAttendance.Roster",
          "Responder",
          "Responder.ResponderPositions",
          "Responder.ResponderPositions.Position",
          "ParsController"
        ],
        pagingInfo: {
          "pageNumber": 0,
          "pageSize": 10000
        },
        sortInfos: [
          {
            columnName: "UpdatedOn",
            isDescending: true
          }
        ]
      };

      if (controllerId) {
        filter.findKeys["ParsControllerId"] = controllerId.toString();
      }

      if (teamName && teamName !== "") {
        filter.findKeys["CurrentEventAttendance.TeamName"] = teamName;
      }

      this.safeSubscribe(this._responderCurrentAttendanceService.FindInDomain(filter),
        data => {
          this._currentAttendances$.next(data);
          return resolve(data);
        }, error => reject(error));
    });
  }

  loadHistoricalAttendancesAtEvent(controllerId: number, teamName: string, date: Date) {
    return new Promise<EventAttendanceModel[]>((resolve, reject) => {
      if (!this.currentEvent) {
        return reject("No current event set for data load");
      }

      const findFilter = {
        findKeys: {
          "EventId": this.currentEvent.id.toString(),
          "ParsControllerId": controllerId ? controllerId.toString() : "",
          "TeamName": teamName,
          "StatusDate ltEq": date ? moment(date.setDate(date.getDate() + 1)).format() : null,
        },
        includes: [
          "Agency", "IcsPosition", "ParsController", "ParsStatus", "Roster", "Responder",
          "Responder.ResponderPositions", "Responder.ResponderPositions.Position"
        ],
        pagingInfo: {
          "pageNumber": 0,
          "pageSize": 10000
        },
        sortInfos: [
          {
            columnName: "UpdatedOn",
            isDescending: true
          }
        ]
      };
      date.setDate(date.getDate() - 1);
      this.safeSubscribe(this._eventAttendanceService.FindInDomain(findFilter), data => {
        this._attendances$.next(data);
        resolve(data);
      }, error => {
        reject(error);
      });
    });
  }

  loadCurrentUser() {
    return new Promise<UserModelEditorViewModel>((resolve, reject) => {
      if (!this.currentUser) {
        this.safeSubscribe(this._userService.GetCurrent(),
          (user: UserModelEditorViewModel) => {
            if (user) {
              this._currentUser$.next(user);
              return resolve(user);
            }
            this._currentUser$.next(undefined);
            return reject('Unable to load current user.');
          }, error => reject(error));
      } else {
        return resolve(this.currentUser);
      }
    });
  }

  loadCurrentUserSettings(user: UserModelEditorViewModel) {
    return new Promise<UserSettingModel>((resolve, reject) => {
      if (user && user.model) {
        this.safeSubscribe(this._userSettingService.FindInDomain({
          findKeys: {
            "UserId": user.model.id.toString()
          }
        }),
          (data: UserSettingModel[]) => {
            if (data && data.length > 0) {
              this._currentUserSetting$.next(data[0]);
              return resolve(data[0]);
            }
            this._currentUserSetting$.next(undefined);
            return resolve(undefined);
          }, error => reject(error));
      } else {
        return resolve(undefined);
      }
    });
  }

  buildHistoricalAttendances(attendances: EventAttendanceModel[], historicalDate: Date) {
    return new Promise<ResponderCurrentAttendanceModel[]>((resolve, reject) => {
      let historicalAttendances: ResponderCurrentAttendanceModel[] = [];
      this.safeSubscribe(this._eventAttendanceService.DistinctStringLookups(), data => {
        if (data) {
          // loop over each unique responder
          historicalDate.setDate(historicalDate.getDate() + 1);
          data.ResponderIds.forEach(responder => {
            let responderEventAttendances = attendances.filter(att => att.responderId === +responder);
            if (responderEventAttendances.length > 0) {
              let attendance = responderEventAttendances[0];
              let currentTime = new Date(historicalDate);
              if (attendance.parsStatusId === this._parsConstants.DEMOBILIZING) {
                currentTime = new Date(attendance.statusDate);
              }
              let checkInTime = new Date(responderEventAttendances[responderEventAttendances.length - 1].statusDate);
              let currentEventAttendance = {
                currentEventAttendance: attendance,
                currentEventAttendanceId: attendance.id,
                eventId: attendance.eventId,
                eventLocationId: attendance.locationId,
                icsPositionId: attendance.icsPositionId,
                parsControllerId: attendance.parsControllerId,
                parsController: attendance.parsController,
                parsStatusId: attendance.parsStatusId,
                responder: attendance.responder,
                responderId: attendance.responderId,
                strikeTeam: attendance.teamName,
                timeAtEvent: Math.round((currentTime.getTime() - checkInTime.getTime()) / 1000),
                updatedOn: attendance.updatedOn
              }
              historicalAttendances.push(currentEventAttendance);
            }
          });
        }
        return resolve(historicalAttendances);
      }, error => {
        reject(error);
      });
    });
  }

  currentUserControllerId(): number {
    if (this.currentUserSetting &&
      this.currentUserSetting.parsControllerId &&
      this.currentUserSetting.parsControllerId > 0) {
      return this.currentUserSetting.parsControllerId;
    }

    return undefined;
  }
}